import { z } from 'zod';
import { queryStatusSchema } from '../enums.js';
import { slugSchema } from './common.js';

export const datasetSummarySchema = z.object({
  slug: z.string(),
  name: z.string(),
  description: z.string(),
  tableCount: z.number().int().nonnegative(),
});
export type DatasetSummary = z.infer<typeof datasetSummarySchema>;

export const tableSummarySchema = z.object({
  name: z.string(),
  description: z.string(),
  /** Planner estimate. An exact count would mean a sequential scan per table. */
  estimatedRows: z.number().int().nonnegative(),
  columnCount: z.number().int().nonnegative(),
  /**
   * Column names only. Included here so the editor has everything it needs for
   * autocomplete from one request, rather than fetching each table's detail.
   */
  columns: z.array(z.string()),
});
export type TableSummary = z.infer<typeof tableSummarySchema>;

export const columnInfoSchema = z.object({
  name: z.string(),
  dataType: z.string(),
  isNullable: z.boolean(),
  defaultValue: z.string().nullable(),
  isPrimaryKey: z.boolean(),
  references: z.object({ table: z.string(), column: z.string() }).nullable(),
});
export type ColumnInfo = z.infer<typeof columnInfoSchema>;

export const tableDetailSchema = z.object({
  name: z.string(),
  description: z.string(),
  estimatedRows: z.number().int().nonnegative(),
  primaryKey: z.array(z.string()),
  columns: z.array(columnInfoSchema),
  sample: z.object({
    columns: z.array(z.string()),
    rows: z.array(z.array(z.string().nullable())),
  }),
});
export type TableDetail = z.infer<typeof tableDetailSchema>;

export const datasetTablesResponseSchema = z.object({
  dataset: datasetSummarySchema,
  tables: z.array(tableSummarySchema),
});
export type DatasetTablesResponse = z.infer<typeof datasetTablesResponseSchema>;

export const datasetParamsSchema = z.object({ slug: slugSchema });
export const tableParamsSchema = z.object({
  slug: slugSchema,
  table: z
    .string()
    .min(1)
    .max(63)
    .regex(/^[a-z_][a-z0-9_]*$/, 'Not a valid table name'),
});

// --- Query history ----------------------------------------------------------

export const queryHistoryEntrySchema = z.object({
  id: z.uuid(),
  datasetSlug: z.string().nullable(),
  sql: z.string(),
  status: queryStatusSchema,
  errorCode: z.string().nullable(),
  executionMs: z.number().int().nullable(),
  rowCount: z.number().int().nullable(),
  createdAt: z.string(),
});
export type QueryHistoryEntry = z.infer<typeof queryHistoryEntrySchema>;

export const queryHistoryQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(25),
  offset: z.coerce.number().int().min(0).default(0),
  /** Narrows to one outcome; omitted means every outcome. */
  status: queryStatusSchema.optional(),
  datasetSlug: z.string().min(1).max(80).optional(),
  /** Case-insensitive substring match against the SQL text. */
  search: z.string().min(1).max(200).optional(),
});
export type QueryHistoryQuery = z.infer<typeof queryHistoryQuerySchema>;

/** Counts per outcome across ALL of a learner's history, ignoring filters. */
export const queryHistoryStatsSchema = z.object({
  total: z.number().int(),
  ok: z.number().int(),
  error: z.number().int(),
  timeout: z.number().int(),
  rejected: z.number().int(),
});
export type QueryHistoryStats = z.infer<typeof queryHistoryStatsSchema>;

export const queryHistoryResponseSchema = z.object({
  items: z.array(queryHistoryEntrySchema),
  /** Total matching the current filters, so the page can say "25 of 340". */
  total: z.number().int(),
  /**
   * Unfiltered counts, returned alongside the page so the filter chips can show
   * how much each one would match without a second round trip.
   */
  stats: queryHistoryStatsSchema,
});
export type QueryHistoryResponse = z.infer<typeof queryHistoryResponseSchema>;
